import { Button } from '@pms-digital/design-system';

export interface ContextStaleBannerProps {
  /** How many cells predate the context change. */
  affectedCellCount: number;
  /** Ask the grid to reveal/highlight the affected cells. */
  onShowAffected: () => void;
  /** Persist the dismissal (D26). */
  onDismiss: () => void;
  /** True while the dismissal request is in flight. */
  isDismissing?: boolean;
  /**
   * Opens `?vue=cadrage` (issue #3). The banner says the framing changed; this
   * is the only control on it that leads to the thing that changed. It used to
   * be an implicit "the Contexte tab is over there in the drawer" — which was
   * never stated, and stopped being true when that tab was removed.
   */
  onOpenCadrage: () => void;
}

/**
 * Matrix-level "your context changed since these cells were generated" banner
 * (2026-08-26 context/digest design doc, D25 + D26).
 *
 * Advisory, never blocking: the matrix stays fully usable, and dismissing is
 * always available. The dismiss control is what makes the per-cell markers
 * disappear too (D26) — hence the explicit `isDismissing`, since that
 * dismissal is a `PATCH .../view-state` round-trip and must not be
 * double-submitted.
 *
 * This does not reuse `<Banner />`: that component types `children` as `string`
 * and feeds it to `aria-label`, so it cannot host the two controls this one
 * needs. The warning surface classes are copied verbatim instead, so the two
 * stay visually identical.
 *
 * Rendering nothing at `affectedCellCount === 0` is part of the contract, so
 * the route can mount this unconditionally and stay dumb about the "no stale
 * cell" case.
 */
export function ContextStaleBanner({
  affectedCellCount,
  onShowAffected,
  onDismiss,
  isDismissing = false,
  onOpenCadrage,
}: ContextStaleBannerProps) {
  if (affectedCellCount <= 0) return null;

  const cellLabel =
    affectedCellCount === 1
      ? '1 cellule est antérieure'
      : `${affectedCellCount} cellules sont antérieures`;

  return (
    // biome-ignore lint/a11y/useSemanticElements: <output> cannot sensibly host the two controls below; role="status" gives the same live-region announcement on the wrapper.
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 rounded-md border-2 border-warning bg-warning-tint px-4 py-3 text-small"
    >
      <p className="grow">
        Le contexte de cette matrice a été modifié depuis la génération. {cellLabel} à ce
        changement.
      </p>
      <Button type="button" size="sm" variant="primary" onClick={onShowAffected}>
        Afficher les cellules concernées
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        iconAfter="arrow_forward"
        onClick={onOpenCadrage}
      >
        Voir le cadrage
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        icon="close"
        aria-label="Masquer cet avertissement de contexte modifié"
        disabled={isDismissing}
        onClick={onDismiss}
      />
    </div>
  );
}
